import './Panel.css'

import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Messages } from 'primereact/messages'
import {
  PanelHeaderTemplateOptions,
  Panel as PrimeReactPanel,
} from 'primereact/panel'

type Props = {
  children: JSX.Element
  formik?: any
  handleCancel: () => void
  handleEdit: () => void
  handleSubmit: () => void
  handleToggle: () => void
  header: any
  visible?: boolean
  isCollapsed: boolean
  isEdit: boolean
  toggleable: boolean
  isRequestRunning?: boolean
  hasChangesPending?: boolean
  buttons?: JSX.Element
}

export const Panel: React.FC<Props> = ({
  children,
  formik,
  handleCancel,
  handleEdit,
  handleSubmit,
  handleToggle,
  header,
  visible = true,
  isCollapsed,
  isEdit,
  toggleable,
  isRequestRunning = false,
  hasChangesPending = false,
  buttons,
}) => {
  const { t } = useTranslation()

  const messagesRef = useRef<Messages>(null)

  useEffect(() => {
    if (hasChangesPending) {
      messagesRef.current?.replace({
        id: 'panel_haschangespending',
        closable: false,
        sticky: true,
        severity: 'warn',
        icon: PrimeIcons.EXCLAMATION_TRIANGLE,
        summary: t(`common.messages.changespending.summary`),
        detail: t(`common.messages.changespending.detail`),
      })
    }
  }, [hasChangesPending])

  const headerTemplate = (options: PanelHeaderTemplateOptions) => {
    const className = `${options.className} justify-content-start`
    const titleClassName = `${options.titleClassName} text-3xl`
    const header = options.props.header

    const toggleClassName = options.togglerClassName
    const toggleIcon = options.collapsed
      ? 'pi pi-chevron-down text-primary'
      : 'pi pi-chevron-up text-primary'

    return (
      <div className={`flex justify-content-between flex-wrap ${className}`}>
        <div>
          <Messages ref={messagesRef} />
          <span className={titleClassName}>{header}</span>
          {toggleable && (
            <Button className={toggleClassName} onClick={handleToggle}>
              <span className={toggleIcon}></span>
            </Button>
          )}
        </div>

        <div>
          {buttons}
          {formik && (
            <>
              {isEdit ? (
                <>
                  <Button
                    key="cancel"
                    type="button"
                    label={t(`common.buttons.cancel`)}
                    aria-label="Cancel"
                    className="p-button-text"
                    onClick={handleCancel}
                    disabled={isRequestRunning}
                  />
                  <Button
                    key="submit"
                    label={t(`common.buttons.save`)}
                    aria-label="Submit"
                    className=" ml-2"
                    type="submit"
                    onClick={handleSubmit}
                    disabled={!formik.dirty || isRequestRunning}
                    icon={
                      isRequestRunning
                        ? `${PrimeIcons.SPINNER} pi-spin`
                        : undefined
                    }
                  />
                </>
              ) : (
                <Button
                  key="edit"
                  type="button"
                  label={t(`common.buttons.edit`)}
                  aria-label="Edit"
                  onClick={handleEdit}
                  disabled={hasChangesPending}
                  text
                />
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      {visible !== false && (
        <PrimeReactPanel
          headerTemplate={headerTemplate}
          header={header}
          className="unstyled-panel mb-3"
          toggleable={toggleable}
          collapsed={isCollapsed}
          onToggle={handleToggle}
        >
          {children}
        </PrimeReactPanel>
      )}
    </>
  )
}
